/**
 * Upload Banking Arche Page
 *
 * Transforms bank transaction CSVs into QuickBooks-compatible format.
 * Source: a Vault Document the user picks from their files, or a CSV
 * uploaded from desktop. Output: a Vault Document in the folder of their
 * Documents they choose in the host Save dialog — the route transforms and
 * saves there in one step.
 */

import React, { useState, useCallback, useRef } from 'react';
import { ArcheHeader } from '../../../components/ArcheHeader';
import { Upload, FolderOpen, Loader2, CheckCircle2, ArrowRight, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { VaultFilePickerDialog, type VaultPickerResult } from '@/components/VaultFilePickerDialog';
import { toast } from '@/components/ui/use-toast';
import { api, BankingTransformResponse } from '@/lib/api';
import { useSaveDialogPrompt } from '@/components/files/file-dialog/useSaveDialogPrompt';

// CSV uploads ride in the JSON request body, so we cap on the client to
// fail fast before the server rejects. Keep this in sync with the route's
// MAX_INLINE_CSV_BYTES.
const MAX_UPLOAD_CSV_BYTES = 1 * 1024 * 1024;

function UploadBankingContent() {
  // Source state. `fileId` is a Vault document id (`vdoc_*`); when the
  // user picks via Desktop we instead carry the inline `csvContent`.
  const [sourceFile, setSourceFile] = useState<{
    name: string;
    fileId?: string;
    csvContent?: string;
  } | null>(null);
  const [filePickerOpen, setFilePickerOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Transform state
  const [isTransforming, setIsTransforming] = useState(false);
  const { promptSave, saveDialog } = useSaveDialogPrompt();
  const [result, setResult] = useState<BankingTransformResponse | null>(null);

  // Handle My-Fias file selection
  const handleMyFiasSelect = useCallback((file: VaultPickerResult) => {
    if (!file.fileName.toLowerCase().endsWith('.csv')) {
      toast({
        title: 'Invalid file type',
        description: 'Please select a CSV file.',
        variant: 'destructive',
      });
      return;
    }
    setSourceFile({ name: file.fileName, fileId: file.documentId });
    setFilePickerOpen(false);
  }, []);

  // Handle desktop file upload
  const handleDesktopFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast({
        title: 'Invalid file type',
        description: 'Please select a CSV file.',
        variant: 'destructive',
      });
      return;
    }

    if (file.size > MAX_UPLOAD_CSV_BYTES) {
      toast({
        title: 'File too large',
        description: `Desktop uploads are limited to ${Math.floor(MAX_UPLOAD_CSV_BYTES / 1024 / 1024)}MB. For larger exports, upload it to your files first and select it from there.`,
        variant: 'destructive',
      });
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setSourceFile({ name: file.name, csvContent: reader.result as string });
    };
    reader.onerror = () => {
      toast({
        title: 'Failed to read file',
        description: 'Could not read the selected file.',
        variant: 'destructive',
      });
    };
    reader.readAsText(file);

    // Reset input so same file can be re-selected
    e.target.value = '';
  }, []);

  // Handle transform
  const handleTransform = useCallback(async () => {
    if (!sourceFile) return;

    setIsTransforming(true);
    setResult(null);

    try {
      // The user chooses where the result goes (a folder of their Documents)
      // and what to do about a name clash; the route transforms and saves
      // there in one step.
      const response = await promptSave({
        suggestedName: `quickbooks-import-${new Date().toISOString().slice(0, 10)}.csv`,
        mimeType: 'text/csv',
        saver: { kind: 'user', label: 'Upload Banking', memoryKey: 'arche_upload_banking' },
        writer: async (save) => {
          const transformed = await api.transformBankingCsv({
            sourceType: sourceFile.fileId ? 'my-fias' : 'upload',
            fileId: sourceFile.fileId,
            csvContent: sourceFile.csvContent,
            save,
          });
          return { ...transformed, documentId: transformed.fileId, name: transformed.fileName };
        },
      });
      if (!response) return;

      setResult(response);

      toast({
        description: `Transformed ${response.rowCount} transactions successfully.`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Transform failed';
      toast({
        title: 'Transform failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsTransforming(false);
    }
  }, [sourceFile, promptSave]);

  return (
    <div className="flex flex-col h-full">
      <ArcheHeader archeId="arche_upload_banking" title="Upload Banking" />
      {saveDialog}
      <div className="flex-1 overflow-auto">
        <div className="max-w-2xl mx-auto p-6 space-y-6">
          {/* Source Section */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Source CSV</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleDesktopFile}
              />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setFilePickerOpen(true)}
                >
                  <FolderOpen className="h-4 w-4 mr-2" />
                  Select from your files
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload from Desktop
                </Button>
              </div>

              {sourceFile && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <span className="truncate">{sourceFile.name}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Transform Button */}
          <Button
            className="w-full"
            size="lg"
            disabled={!sourceFile || isTransforming}
            onClick={handleTransform}
          >
            {isTransforming ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Transforming...
              </>
            ) : (
              <>
                <ArrowRight className="h-4 w-4 mr-2" />
                Transform for QuickBooks
              </>
            )}
          </Button>

          {/* Result Section */}
          {result && (
            <Card className="border-green-200 dark:border-green-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  Transform Complete
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-sm space-y-1">
                  <p>
                    <span className="text-muted-foreground">Transactions:</span> {result.rowCount}
                  </p>
                  <p>
                    <span className="text-muted-foreground">Output:</span> {result.fileName}
                  </p>
                  <p>
                    <span className="text-muted-foreground">Format:</span> {result.adapterName}
                  </p>
                </div>

                {/* Preview table */}
                {result.preview.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">Preview</p>
                    <div className="overflow-x-auto rounded border">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-muted/50">
                            {result.preview[0].split(',').map((col: string, i: number) => (
                              <th key={i} className="px-3 py-2 text-left font-medium">
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {result.preview.slice(1).map((row: string, i: number) => (
                            <tr key={i} className="border-t">
                              {row.split(',').map((cell: string, j: number) => (
                                <td key={j} className="px-3 py-2">
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/30 p-3 rounded">
                  <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>
                    “{result.fileName}” is saved in your Documents. You can import it into
                    QuickBooks Online via the QuickBooks Banking tab on the QuickBooks website. From
                    there you can upload your transactions.
                  </span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* My-Fias Vault File Picker */}
      <VaultFilePickerDialog
        open={filePickerOpen}
        onOpenChange={setFilePickerOpen}
        title="Select CSV File"
        description="Choose a bank transaction CSV from your files."
        onFileSelected={handleMyFiasSelect}
      />
    </div>
  );
}

export function UploadBankingPage() {
  return <UploadBankingContent />;
}

export default UploadBankingPage;
